import React, { useCallback, useState } from 'react';
import './App.css';
import SearchForm from './components/SearchForm';
import ResultsTable from './components/ResultsTable';
import Statistics from './components/Statistics';
import UserReportForm from './components/UserReportForm';
import RecentSearches from './components/RecentSearches';
import RagAsk from './components/RagAsk';
import { getApiUrl, getConfig } from './config';
import { Search, User, Sparkles } from 'lucide-react';
import logo from './logo.svg';

function App() {
  const [activeTab, setActiveTab] = useState('search');
  const [searchResults, setSearchResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [initialSearchParams, setInitialSearchParams] = React.useState(null);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(false);
  const [totalEstimate, setTotalEstimate] = React.useState(null);
  const [currentSearchParams, setCurrentSearchParams] = React.useState(null);
  const [searchCompleted, setSearchCompleted] = React.useState(false);
  const searchFormRef = React.useRef(null);
  
  const config = getConfig();

  const cleanSearchParams = useCallback((searchParams) => {
    const cleaned = {
      limit: searchParams.limit || config.app.defaultLimit,
      offset: searchParams.offset || 0
    };
    if (searchParams.author) cleaned.author = searchParams.author;
    if (searchParams.content_search) cleaned.content_search = searchParams.content_search;
    if (searchParams.datetime) cleaned.datetime = searchParams.datetime;
    if (searchParams.rating_plus) cleaned.rating_plus = searchParams.rating_plus;
    if (searchParams.rating_minus) cleaned.rating_minus = searchParams.rating_minus;
    if (searchParams.haveimages) cleaned.haveimages = searchParams.haveimages;
    if (searchParams.havevideos) cleaned.havevideos = searchParams.havevideos;
    return cleaned;
  }, [config]);

  const updateUrl = useCallback((params) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (params.tab !== undefined) {
      if (params.tab) {
        urlParams.set('tab', params.tab);
      } else {
        urlParams.delete('tab');
      }
    }
    
    if (params.username !== undefined) {
      if (params.username) {
        urlParams.set('username', params.username);
      } else {
        urlParams.delete('username');
      }
    }
    
    if (params.year !== undefined) {
      if (params.year) {
        urlParams.set('year', params.year);
      } else {
        urlParams.delete('year');
      }
    }
    
    const newUrl = urlParams.toString() 
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, []);

  const formatSearchCriteria = useCallback((params) => {
    const parts = [];
    if (params?.author) parts.push(`автор: ${params.author}`);
    if (params?.content_search) parts.push(`текст: «${params.content_search}»`);
    return parts.join(', ');
  }, []);

  const handleSearch = useCallback(async (searchParams) => {
    setLoading(true);
    setError(null);
    setSearchCompleted(false);
    setSearchResults([]);
    setNextCursor(null);
    setHasMore(false);
    setTotalEstimate(null);

    const urlParams = new URLSearchParams();
    if (searchParams.author) {
      urlParams.set('author', searchParams.author);
    }
    if (searchParams.content_search) {
      urlParams.set('content_search', searchParams.content_search);
    }
    const newUrl = urlParams.toString() 
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);

    const params = cleanSearchParams(searchParams);
    setInitialSearchParams(params);

    try {
      const apiUrl = getApiUrl(config.api.endpoints.search);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSearchResults(data.data.comments || []);
        setNextCursor(data.data.next_cursor ?? null);
        setHasMore(!!data.data.has_more);
        setTotalEstimate(
          typeof data.data.total_estimate === 'number' ? data.data.total_estimate : null
        );
        setCurrentSearchParams(params);
        setSearchCompleted(true);
      } else {
        throw new Error(data.error || 'Ошибка поиска');
      }
    } catch (err) {
      setError(err.message);
      setSearchResults([]);
      setNextCursor(null);
      setHasMore(false);
      setTotalEstimate(null);
      setCurrentSearchParams(null);
      setSearchCompleted(false);
    } finally {
      setLoading(false);
    }
  }, [config, cleanSearchParams]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || loadingMore || !currentSearchParams) {
      return;
    }
    setLoadingMore(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(config.api.endpoints.search);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...currentSearchParams, cursor: nextCursor }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSearchResults(prev => [...prev, ...(data.data.comments || [])]);
        setNextCursor(data.data.next_cursor ?? null);
        setHasMore(!!data.data.has_more);
      } else {
        throw new Error(data.error || 'Ошибка поиска');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [config, nextCursor, loadingMore, currentSearchParams]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const author = urlParams.get('author');
    const content_search = urlParams.get('content_search');

    if (tab === 'report') {
      setActiveTab('report');
    } else if (tab === 'rag') {
      setActiveTab('rag');
    }

    if (author || content_search) {
      const params = {
        limit: config.app.defaultLimit,
        offset: 0
      };
      if (author) params.author = author;
      if (content_search) params.content_search = content_search;
      setInitialSearchParams(params);
      handleSearch(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchFromRecent = useCallback(async (searchParams) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    const urlParams = new URLSearchParams();
    if (searchParams.author) {
      urlParams.set('author', searchParams.author);
    }
    if (searchParams.content_search) {
      urlParams.set('content_search', searchParams.content_search);
    }
    const newUrl = urlParams.toString() 
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);

    const params = cleanSearchParams(searchParams);
    setInitialSearchParams(params);
    
    setTimeout(() => {
      if (searchFormRef.current && searchFormRef.current.triggerSearch) {
        searchFormRef.current.triggerSearch();
      }
    }, 100);
  }, [config, cleanSearchParams]);

  return (
    <div className="App">
      <header className="App-header">
        <a href="/" className="App-header-link">
          <div className="App-header-content">
            <img src={logo} alt="Pikabulling Logo" className="App-logo" />
            <div className="App-header-text">
              <h1>{config.app.name}</h1>
              <p>Поиск комментариев с расширенными фильтрами и досье пользователей</p>
            </div>
          </div>
        </a>
      </header>
      
      <main className="App-main">
        <div className="tabs-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'rag' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('rag');
                updateUrl({ tab: 'rag', username: '', year: '' });
              }}
            >
              <Sparkles size={20} />
              Вопрос-ответ (RAG)
            </button>
            <button
              className={`tab ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('search');
                updateUrl({ tab: '', username: '', year: '' });
              }}
            >
              <Search size={20} />
              Поиск комментариев
            </button>
            <button
              className={`tab ${activeTab === 'report' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('report');
                const urlParams = new URLSearchParams(window.location.search);
                const currentUsername = urlParams.get('username');
                updateUrl({ tab: 'report', username: currentUsername || '' });
              }}
            >
              <User size={20} />
              Досье пользователя
            </button>
          </div>
        </div>

        {activeTab === 'rag' && (
          <RagAsk />
        )}

        {activeTab === 'search' && (
          <>
            <SearchForm ref={searchFormRef} onSearch={handleSearch} initialParams={initialSearchParams} />
            
            {error && (
              <div className="error-message">
                <strong>Ошибка:</strong> {error}
              </div>
            )}

            {loading && (
              <div className="loading">
                <div className="spinner"></div>
                <p>Поиск...</p>
              </div>
            )}

            {!loading && !error && searchCompleted && searchResults.length === 0 && (
              <div className="no-results">
                <h2>Ничего не найдено</h2>
                <p>Комментарии по вашему запросу не найдены.</p>
                {currentSearchParams && formatSearchCriteria(currentSearchParams) && (
                  <p className="no-results-criteria">
                    {formatSearchCriteria(currentSearchParams)}
                  </p>
                )}
              </div>
            )}

            {searchResults.length > 0 && (
              <div className="results-section">
                <h2>
                  Результаты поиска (загружено {searchResults.length}
                  {totalEstimate !== null && totalEstimate >= searchResults.length
                    ? ` из ~${totalEstimate.toLocaleString(config.ui.locale)}`
                    : ''}
                  )
                </h2>
                <ResultsTable 
                  comments={searchResults} 
                  onAuthorClick={(author) => {
                    const searchParams = {
                      author: author,
                      limit: config.app.defaultLimit,
                      offset: 0
                    };
                    handleSearch(searchParams);
                  }}
                />
                {hasMore && (
                  <div className="load-more-container">
                    <button
                      type="button"
                      className="load-more-btn"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <Statistics />
            
            <RecentSearches 
              onSearchClick={handleSearchFromRecent}
              onAuthorClick={(author, commentsRange) => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setActiveTab('report');
                const year = commentsRange && commentsRange !== 'all' ? commentsRange : '';
                updateUrl({ tab: 'report', username: author, year: year });
              }}
            />
          </>
        )}

        {activeTab === 'report' && (
          <UserReportForm 
            initialUsername={new URLSearchParams(window.location.search).get('username') || ''}
            initialYear={new URLSearchParams(window.location.search).get('year') || ''}
            onUrlUpdate={updateUrl}
          />
        )}
      </main>
    </div>
  );
}

export default App;
